import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, UserPlus, Loader2 } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success(`Bem-vindo, ${user.full_name}!`);
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Erro ao fazer login';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4">
      <div className="grid-overlay" />

      <div className="w-full max-w-md z-10 slide-up">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Logo size="lg" />
        </div>
        <div className="flex justify-center mb-8">
          <span className="hutoo-eyebrow">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            A Alta Performance tem método
          </span>
        </div>

        {/* Login Card */}
        <Card className="glass-card border-white/10 rounded-3xl">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-white">Acesse sua conta</CardTitle>
            <CardDescription className="text-neutral-500">
              Entre com suas credenciais para continuar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-neutral-500 uppercase text-xs tracking-wide">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-neutral-900/50 border-neutral-800 text-white placeholder:text-neutral-600 h-12"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-neutral-500 uppercase text-xs tracking-wide">
                  Senha
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-neutral-900/50 border-neutral-800 text-white placeholder:text-neutral-600 h-12 pr-12"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="group w-full h-12 btn-primary"
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    Entrar
                    <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={18} />
                  </>
                )}
              </Button>

              <button
                type="button"
                onClick={() => navigate('/esqueci-senha')}
                className="w-full text-sm text-neutral-400 hover:text-white transition-colors"
                data-testid="forgot-password-link"
              >
                Esqueci minha senha
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-neutral-500 text-sm mb-4">
                Ainda não tem conta?
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/form')}
                className="w-full h-11 text-white"
                data-testid="register-link"
              >
                <UserPlus className="mr-2" size={18} />
                Iniciar cadastro
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-neutral-600 text-sm mt-8">
          Hutoo Educação © {new Date().getFullYear()} · Programa Elite
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
